import { axiosInstance } from "../../../api/axiosInstance.tsx";

export interface OfficeSearchParams {
  keyword?: string;
  officeName?: string;
  officeAddress?: string;
  region?: string;
  officeType?: string;
  officeEmail?: string;
  managerName?: string;
  contactNumber?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  order?: string;
}

export const getOffices = async (params: OfficeSearchParams = {}) => {
  const response = await axiosInstance.get("/offices/search_offices", {
    params,
  });
  return response.data;
};

export const getAllOffices = async () => {
  const response = await axiosInstance.get("/offices/view_offices");
  return response.data;
};

export interface OfficeLocationSearchParams {
  keyword?: string;
  officeId?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  order?: string;
}

export const getOfficeLocations = async (params: OfficeLocationSearchParams = {}) => {
  const response = await axiosInstance.get("/offices/search_offices", {
    params,
  });
  return response.data;
};


export const fetchOfficePhotoBlob = async (filename: string): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/offices/photo/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};



// Step 1: Create Office
export const createOffice = async (data) => {
  const formData = {
    officeName: data.officeName,
    officeAddress: data.officeAddress,
    region: data.region,
    officeType: data.officeType,
    officeEmail: data.officeEmail,
    managerName: data.managerName,
    contactNumber:data.contactNumber,
  };

  const response = await axiosInstance.post(`/offices/create_office`, formData);
  return response.data; // contains the office `id` or `code`
};

// Create Office Location
export const createOfficeLocation = async (data) => {
  
  const response = await axiosInstance.post(`/officeLocations/create_office_location`, data);
  return response.data; // contains the office `id` or `code`
};

// Step 2: Upload Photo by Office ID
export const uploadOfficePhoto = async (officeId, file) => {
  const formData = new FormData();
  formData.append("photo", file);

  return axiosInstance.post(`/offices/upload_photo/${officeId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Di dalam uploadOfficeAttachments service, tambahkan:
export const uploadOfficeAttachments = async (
  officeId: string,
  files: File[],
  description?: string
) => {
  try {
    if (!officeId) throw new Error("officeId diperlukan");
    if (files.length > 10) throw new Error("Maksimum 10 file diperbolehkan");

    const formData = new FormData();

    // Debug: Log semua files
    console.log("Files to upload:", files);

    // Append setiap file
    files.forEach((file, index) => {
      formData.append("attachments", file);
      console.log(`Appended file ${index}:`, file.name, file.size, file.type);
    });

    if (description) {
      formData.append("description", description);
      console.log("Appended description:", description);
    }

    // Debug FormData contents
    console.log("FormData entries:");
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    const response = await axiosInstance.post(
      `/offices/offices/${officeId}/attachments`, 
      formData, 
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 60000, // 60 detik timeout
      }
    );

    console.log("Upload response:", response.data);
    return response.data;

  } catch (error) {
    console.error("Upload attachment error:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

// Helper function untuk convert File ke base64 string
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // reader.result format: "data:image/png;base64,iVBORw0KGg..."
      const base64String = reader.result as string;
      // Hapus prefix data URL jika perlu, atau kirim lengkap
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
  });
};

// services/officeService.tsx - Pastikan interface ini ada
export interface OfficeAttachment {
  attachmentId: string;
  officeId: string;
  originalName: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * GET list attachments for an office
 * GET /api/offices/offices/{id}/attachments
 */
export const getOfficeAttachments = async (
  officeId: string
): Promise<OfficeAttachment[]> => {
  if (!officeId) throw new Error("officeId diperlukan");
  const response = await axiosInstance.get(`/offices/offices/${officeId}/attachments`, {
    // headers: { Accept: "application/json" } // opsional
  });
  return response.data as OfficeAttachment[];
};

/**
 * Download specific attachment as a blob and return an object URL
 * GET /api/offices/attachments/{attachmentId}/download
 */
export const downloadAttachmentBlob = async (
  attachmentId: string
): Promise<{ objectUrl: string; mimeType: string; filename: string } | null> => {
  if (!attachmentId) throw new Error("attachmentId diperlukan");
  try {
    const response = await axiosInstance.get(
      `/offices/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
      }
    );

    const mimeType = response.data.type || "application/octet-stream";
    // Try to read filename from content-disposition header, fallback to attachmentId
    const disposition = response.headers?.["content-disposition"] || "";
    let filename = attachmentId;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i.exec(disposition);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }

    const objectUrl = URL.createObjectURL(response.data);
    return { objectUrl, mimeType, filename };
  } catch (err) {
    console.error("Gagal download attachment:", err);
    return null;
  }
};

/**
 * Helper to download and auto-save file on client
 */
export const downloadAttachmentFile = async (attachmentId: string) => {
  const result = await downloadAttachmentBlob(attachmentId);
  if (!result) throw new Error("Gagal mengunduh file");

  const link = document.createElement("a");
  link.href = result.objectUrl;
  link.download = result.filename || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // optionally revoke the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(result.objectUrl), 1000 * 30);
};

/**
 * DELETE specific attachment
 * DELETE /api/offices/attachments/{attachmentId}
 */
export const deleteAttachment = async (attachmentId: string) => {
  if (!attachmentId) throw new Error("attachmentId diperlukan");
  const response = await axiosInstance.delete(`/offices/attachments/${attachmentId}`);
  return response.data;
};




// Edit Office
export const editOffice = async (data) => {
  const formData = {
    officeName: data.officeName,
    officeAddress: data.officeAddress,
    region: data.region,
    officeType: data.officeType,
    officeEmail: data.officeEmail,
    managerName: data.managerName,
    contactNumber: data.contactNumber,
  };

  const response = await axiosInstance.put(`/offices/update_office/${data.officeId}`, formData);
  return response.data; // contains the office `id` or `code`
};

export const deleteOffice = async (officeId: string) => {
  const response = await axiosInstance.delete(`/offices/delete_office/${officeId}`);
  return response.data;
};
